/**
 * Phase 32 Plan 32-2 — admin "Unmapped Commits" page.
 *
 * Surface inventory (UI-SPEC §195-204):
 *   /admin/engineering/unmapped     AdminRoute → THIS PAGE
 *
 * Lists GitHub logins seen in commits whose `authorUserId IS NULL`. Each row
 * includes a "Map user →" deeplink to the admin user search pre-filled with
 * the login. After the admin sets the user's `githubUsername`, Plan 32-1b's
 * userAdminService side-effect enqueues a `reresolve_user` job that flips
 * historical commits to the new userId.
 */
import type { ReactElement } from 'react';
import { Spinner } from '../../components/common/Spinner';
import { UnmappedCommitsTable } from '../../components/engineering/UnmappedCommitsTable';
import { useUnmappedCommits } from '../../hooks/useEngineeringMetrics';

export default function UnmappedCommitsPage(): ReactElement {
  const q = useUnmappedCommits();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Unmapped Commits</h1>
        <p className="mt-1 text-sm text-gray-600">
          GitHub logins seen on commits that could not be linked to a Monksheets
          user. Map a login to a user (set their <code>githubUsername</code> in
          User Admin) and the next reresolve job will flip historical commits.
        </p>
      </div>

      {q.isLoading && (
        <div className="flex items-center justify-center h-32">
          <Spinner />
        </div>
      )}
      {q.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load unmapped commits: {(q.error as Error).message}
        </div>
      )}
      {q.data && <UnmappedCommitsTable rows={q.data} />}
    </div>
  );
}
